import React from 'react';
import { Building2, Users, TrendingUp, Shield, Clock, DollarSign } from '../lib/icons';
import Contact from '../components/Contact';
import BusinessPricing from '../components/BusinessPricing';

function BulkServicesPage() {
  const benefits = [
    {
      icon: DollarSign,
      title: 'Volume Discounts',
      description: 'Save 15-30% on repairs when you send 5+ devices at once'
    },
    {
      icon: Clock,
      title: 'Priority Service',
      description: 'Fast-track turnaround times for business and institutional clients'
    },
    {
      icon: Shield,
      title: 'Extended Warranties',
      description: '90-day warranty on all bulk repairs, with optional extended coverage'
    },
    {
      icon: TrendingUp,
      title: 'Fleet Management',
      description: 'Track repair history and maintenance schedules for all your devices'
    }
  ];

  const idealFor = [
    {
      icon: Building2,
      title: 'Schools & Universities',
      description: 'Gaming labs, esports programs, and student lounges with multiple consoles'
    },
    {
      icon: Users,
      title: 'Esports Organizations',
      description: 'Teams and training facilities managing competitive gaming equipment'
    },
    {
      icon: Building2,
      title: 'Gaming Cafes',
      description: 'Commercial venues with multiple consoles requiring regular maintenance'
    },
    {
      icon: Building2,
      title: 'Rental Companies',
      description: 'Event companies and rental services maintaining gaming equipment fleets'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Bulk Repair & Buyback Services
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Specialized services for organizations managing multiple gaming consoles. From volume repairs to device buyback programs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-[#1A1A1A] p-8 rounded-lg border border-gray-800 hover:border-red-900 transition-all"
            >
              <benefit.icon className="w-12 h-12 text-red-500 mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
              <p className="text-gray-400">{benefit.description}</p>
            </div>
          ))}
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Ideal For
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {idealFor.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-red-900/10 to-orange-900/10 p-6 rounded-lg border border-red-900/20"
              >
                <item.icon className="w-10 h-10 text-red-400 mb-3" />
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-[#1A1A1A] p-8 rounded-lg border border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-6">Bulk Repair Program</h2>
            <div className="space-y-4">
              <div className="border-l-4 border-red-600 pl-4">
                <h3 className="font-semibold text-white mb-1">5-9 Devices</h3>
                <p className="text-gray-400">15% discount on all repairs</p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h3 className="font-semibold text-white mb-1">10-19 Devices</h3>
                <p className="text-gray-400">20% discount on all repairs</p>
              </div>
              <div className="border-l-4 border-red-600 pl-4">
                <h3 className="font-semibold text-white mb-1">20+ Devices</h3>
                <p className="text-gray-400">30% discount + priority service</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Free diagnostic on all devices. Pay only for repairs you approve.
            </p>
          </div>

          <div className="bg-[#1A1A1A] p-8 rounded-lg border border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-6">Device Buyback Program</h2>
            <p className="text-gray-400 mb-6">
              Upgrading your gaming equipment? We purchase working and non-working consoles in bulk.
            </p>
            <div className="space-y-3">
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                Competitive bulk pricing
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                Fast evaluation and payment
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                Trade-in credit available
              </div>
              <div className="flex items-center text-gray-300">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                We purchase any condition
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-900/20 to-orange-900/20 border border-red-900/30 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-white mb-4 text-center">
            Ready to Partner With Us?
          </h2>
          <p className="text-gray-300 text-center mb-8 max-w-2xl mx-auto">
            Contact us for a custom quote tailored to your organization's needs. We'll work with you to create a maintenance plan that keeps your gaming equipment running smoothly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/#contact"
              className="px-8 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors text-center"
            >
              Request Bulk Quote
            </a>
            <a
              href="tel:+17602147244"
              className="px-8 py-3 bg-[#1A1A1A] text-white font-semibold rounded-lg border border-gray-700 hover:border-red-600 transition-colors text-center"
            >
              Call: (760) 214-7244
            </a>
          </div>
        </div>

        <BusinessPricing />

        <div className="mt-16">
          <Contact />
        </div>
      </div>
    </div>
  );
}

export default BulkServicesPage;
